import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import { LIST_USER_GROUPS_QUERY, DELETE_USER_GROUP_MUTATION } from '../../pages/clients/gql';
import { dispatchException, dispatchMessage } from '../../helper/snackbar';
import CreateEditUserGroupDialog from './CreateEditUserGroupDialog';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';

interface UserGroupsSectionProps {
  publicCompanyId: number;
  companyName: string;
}

/**
 * Section component for managing user groups associated with a company.
 * Displays user groups list and allows creation/editing/deletion.
 */
export default function UserGroupsSection({ publicCompanyId, companyName }: UserGroupsSectionProps) {
  const dispatch = useDispatch();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    group?: any;
  }>({ open: false });

  const { data: userGroupsData, loading, refetch } = useQuery(LIST_USER_GROUPS_QUERY, {
    variables: {
      where: { publicCompanyId },
    },
    fetchPolicy: 'cache-and-network',
  });

  const [deleteUserGroup] = useMutation(DELETE_USER_GROUP_MUTATION);

  const handleCreateGroup = () => {
    setShowCreateDialog(true);
  };

  const handleEditGroup = (group: any) => {
    setEditingGroup(group);
  };

  const handleDeleteGroup = (group: any) => {
    setDeleteConfirm({ open: true, group });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.group) return;

    try {
      await deleteUserGroup({
        variables: { id: deleteConfirm.group.id },
      });
      
      dispatchMessage(dispatch, i18next.t('usergroup-deleted-success'));
      refetch();
      setDeleteConfirm({ open: false });
    } catch (error: any) {
      dispatchException(dispatch, error);
    }
  };

  const handleDialogClose = () => {
    setShowCreateDialog(false);
    setEditingGroup(null);
    refetch();
  };

  const userGroups = userGroupsData?.listUserGroups || [];

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {i18next.t('usergroup-title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateGroup}
          size="small"
        >
          {i18next.t('usergroup-add-button')}
        </Button>
      </Box>

      {loading ? (
        <Typography>{i18next.t('loading')}</Typography>
      ) : userGroups.length === 0 ? (
        <Alert severity="info">
          {i18next.t('usergroup-no-groups')}
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18next.t('usergroup-list-name')}</TableCell>
                <TableCell align="center">{i18next.t('usergroup-list-members-count')}</TableCell>
                <TableCell align="center">{i18next.t('usergroup-list-actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userGroups.map((group: any) => (
                <TableRow key={group.id}>
                  <TableCell>{group.name}</TableCell>
                  <TableCell align="center">{group.membersCount || 0}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditGroup(group)}
                      title={String(i18next.t('edit'))}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteGroup(group)}
                      title={String(i18next.t('delete'))}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      {(showCreateDialog || editingGroup) && (
        <CreateEditUserGroupDialog
          open={true}
          onClose={handleDialogClose}
          publicCompanyId={publicCompanyId}
          userGroup={editingGroup}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirm.open}
        setOpen={(open) => setDeleteConfirm({ open, group: open ? deleteConfirm.group : undefined })}
        onConfirm={confirmDelete}
        title={i18next.t('usergroup-delete-confirm-title')}
      >
        {i18next.t('usergroup-delete-confirm-message', { name: deleteConfirm.group?.name || '' })}
      </ConfirmationDialog>
    </Paper>
  );
}